export const createDebouncedApiSearch = (searchFunction, options = {}) => {
  const {
    initialDelay = 100,
    minDelay = 100,
    maxDelay = 3000,
    responseTimeMultiplier = 0.5,
    inactivityResetTime = 2000, // Reset to initial delay after 2s of inactivity
  } = options;

  let searchTimeout = null;
  let lastResponseTime = initialDelay;
  let currentAbortController = null;
  let lastCallTime = 0;

  const debouncedFunction = (...args) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (currentAbortController) {
      currentAbortController.abort();
      currentAbortController = null;
    }

    // Reset to initial delay if there's been a period of inactivity
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime;
    if (timeSinceLastCall > inactivityResetTime) {
      lastResponseTime = initialDelay;
    }
    lastCallTime = now;

    const dynamicDelay = lastResponseTime;

    searchTimeout = setTimeout(async () => {
      const startTime = Date.now();

      currentAbortController = new AbortController();
      const abortSignal = currentAbortController.signal;

      try {
        await searchFunction(abortSignal, ...args);

        const responseTime = Date.now() - startTime;
        const newDelay = Math.min(
          Math.max(responseTime * responseTimeMultiplier, minDelay),
          maxDelay,
        );
        lastResponseTime = newDelay;
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error in debounced search:", error);
        }
      }
    }, dynamicDelay);
  };

  debouncedFunction.clearTimeout = () => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }
  };

  return debouncedFunction;
};

export class AbortControllerMap {
  constructor() {
    this.controllers = new Map();
  }

  create(key) {
    this.abort(key);
    const controller = new AbortController();
    this.controllers.set(key, controller);
    return controller;
  }

  get(key) {
    return this.controllers.get(key);
  }

  abort(key) {
    const controller = this.controllers.get(key);
    if (controller) {
      controller.abort();
      this.controllers.delete(key);
    }
  }

  abortAll() {
    this.controllers.forEach((controller) => controller.abort());
    this.controllers.clear();
  }

  cleanup(key) {
    this.controllers.delete(key);
  }

  static isAbortError(error) {
    return (
      error &&
      (error.name === "AbortError" ||
        error.name === "CanceledError" ||
        error.code === "ERR_CANCELED")
    );
  }

  static isAborted(signal) {
    return signal && signal.aborted;
  }
}
